import { ParsedUrlQuery } from 'querystring';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import moment from 'moment';
import { SwiperSlide } from 'swiper/react';

import Typography from '@components/Typography';
import { Col, Separator } from '@components/formStyles';
import {
  InfoIcon,
  AlertIcon,
  DownArrowIcon,
  UpArrowIcon,
  WorldLogoWhite,
} from '@assets/icons';
import BorderedLabel from '@components/ui/labels/bordered/BorderedLabel';
import { Discount } from '@components/ui/labels/discount/DiscountLabelStyle';
import Alert, {
  AlertType,
} from '@components/views/hotel/detail/components/room-selection/components/Alert';
import Price from '@components/ui/price';
import {
  getAvailableStatus,
  typeToName,
  getCurrencySymbol,
} from '@utils/utils';
import {
  C4CSalesItemType,
  HotelAvailabilityStatusType,
  LoyaltyType,
  RoomCancellationPolicyDetailViewModel,
  RoomExpediaPriceDetailViewModel,
  ExpediaNonRefundableDatesViewModel,
  RoomVariationViewModel,
  CheckAvailabilityViewModel,
  BankPosOpportunityInfoDataViewModel,
} from '@api/v3';
import { Currencies, MAX_SETUR_POINTS_EARN } from '@constants/constants';
import {
  DateType,
  formatISODateGTM,
  formatISOToDate,
  formatMomentDate,
  getTimeOfISODate,
  GTM_DATE_FORMAT,
  DEFAULT_DATE_FORMAT,
} from '@utils/dateUtil';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import {
  addHotelLetUsCallYouClickEventToGTM,
  addHotelLetUsCallYouSuccessEventToGTM,
  addHotelRoomSelectionClickEventToGTM,
} from '@utils/gtm/hotel-gtm';
import FeeDetails from '@components/ui/fee/Details/Details';
import { useAuthState } from '@contexts/auth/AuthContext';
import {
  shouldHideLoyalty,
  requiredAuthentication,
  checkLoyaltyPreviouslyValidated,
} from '@components/ui/loyalty-verifications/helpers';
import { useToggle } from '@hooks/common';
import { useDevice } from '@contexts/device';
import CustomIcon from '@components/utils/CustomIcon';
import { useChatGPTContext } from '@contexts/chatGPT';
import { signUpGtm } from '@utils/gtm/membership-gtm';
import { paramsToObject } from '@components/views/utils';
import palette from '@utils/theme/palette';
import Tooltip from '@components/ui/Tooltip/next';

import {
  Wrapper,
  Title,
  Description,
  DistantRow,
  RightCol,
  LabelWrapper,
  StyledButtonMobile,
  StyledButtonDesktop,
  WarningWrapper,
  StyledIconLabel,
  MobileIconLabelWrapper,
  MobileWrapperForCancellation,
  Circle,
  ChildPriceDesc,
  ChildPriceWrapper,
  DistantColumn,
  InlineContainer,
  WorldPriceWrapper,
  SeparatorWorld,
  WorldPriceTextWrapper,
  WorldPriceWrapperMobile,
  WorldPriceContainerMobile,
  WorldPriceTextWrapperMobile,
  PromDescription,
  PromDescriptionTitle,
  RoomCardInfoWrapper,
} from './RoomSelectionCardStyle';
import { useSetModalLoyaltyType } from '../../context';
import ReadMoreDescription from './components/ReadMoreDescription';
import {
  ButtonWrapper,
  DailyDiscountedButton,
  PriceContainer,
  StyledSwiper,
} from './PriceCardStyle';
import PriceCard from './PriceCard';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation';
import { getDateStatus } from './helpers';
import DateStatusTypes from './components/AvailableRoomDates/DateStatusTypes';

const OriginalPrice = dynamic(
  () => import('@components/ui/price/OriginalPrice')
);

const LetUsCallYou = dynamic(
  () => import('@components/ui/LetUsCallYou/LetUsCallYou')
);

const LoyaltyEarnBadge = dynamic(
  () => import('@components/LoyaltyBadge/LoyaltyBadge')
);

const ExpediaPrice = dynamic(() => import('@components/ui/price/ExpediaPrice'));

const LoginRedirectModal = dynamic(() =>
  import('@components/ui/loyalty-verifications/helpers').then(
    ({ LoginRedirectModal }) => LoginRedirectModal
  )
);

export interface RoomSelectionCardProps {
  id?: number;
  hotelName?: string;
  title?: string;
  promotionInfoName?: string;
  prices?: {
    originalPrice?: number;
    price?: number;
    currency?: string;
    originalCurrency?: string;
    discount?: string;
  };
  hasCancelletion?: boolean;
  roomInformation?: string;
  status?: HotelAvailabilityStatusType;
  onSelectVariation?: () => void;
  isSelected?: boolean;
  loyalty?: {
    isActive?: boolean;
    loyaltyType?: LoyaltyType;
  };
  warningText?: string;
  alertMessage?: string;
  cancellationPolicyDetails?: Array<RoomCancellationPolicyDetailViewModel>;
  nonRefundableDates?: Array<ExpediaNonRefundableDatesViewModel>;

  openDatePicker?: React.Dispatch<React.SetStateAction<boolean>>;
  formCheckInDate?: DateType | null;
  formCheckOutDate?: DateType | null;
  hotelRoomList?: Array<ListingSearchRoomInterface>;
  feeDetails?: RoomExpediaPriceDetailViewModel;
  isExpedia?: boolean;
  roomSquareMeter?: string;
  description?: string;
  childWithPriceCount?: number;
  loyaltyDiscount?: number;
  loyaltyEarn?: number;
  dailyDiscountedPrice?: RoomVariationViewModel;
  roomId?: number;
  availableDates?: CheckAvailabilityViewModel;
  bankPosOpportunityInfo?: BankPosOpportunityInfoDataViewModel;
  bankPosPromotionRate?: number;
  basketHotelCount?: number;
}

interface ButtonActionType {
  text: string;
  function?: () => void;
}

const cancellationPolicies =
  (policies: RoomCancellationPolicyDetailViewModel[]) => () =>
    (
      <>
        {policies?.map((policy, key) => (
          <li key={key}>{policy.description}</li>
        ))}
      </>
    );
const showPrice = (status: HotelAvailabilityStatusType): boolean => {
  switch (status) {
    case HotelAvailabilityStatusType.MAX_PEOPLE_EXCEEDED:
    case HotelAvailabilityStatusType.ACCOMMODATION_TOO_LONG:
    case HotelAvailabilityStatusType.RESTRICTED:
      return false;
    default:
      return true;
  }
};

const RoomSelectionCard = ({
  id,
  hotelName,
  title,
  promotionInfoName,
  prices,
  hasCancelletion,
  roomInformation = '',
  status,
  onSelectVariation,
  isSelected = false,
  loyalty,
  warningText,
  alertMessage,
  cancellationPolicyDetails,
  nonRefundableDates,
  openDatePicker,
  formCheckInDate,
  formCheckOutDate,
  hotelRoomList,
  feeDetails,
  isExpedia,
  roomSquareMeter,
  childWithPriceCount,
  description,
  loyaltyDiscount,
  loyaltyEarn,
  dailyDiscountedPrice,
  availableDates,
  bankPosOpportunityInfo,
  bankPosPromotionRate,
  basketHotelCount,
}: RoomSelectionCardProps) => {
  const { loginInfo } = useAuthState();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const isLoggedIn = !!loginInfo?.token;

  const [isLetUsCallYouModalVisible, setIsLetUsCallYouModalVisible] =
    useState(false);
  const [showLoginRedirectionModal, , setShowLoginRedirectionModal] =
    useToggle(false);
  const [isShow, setIsShow] = useState(true);
  const [counter, setCounter] = useState(0);
  const setModalLoyaltyType = useSetModalLoyaltyType();
  const isPriceAvailable = useCallback(showPrice, [status]);
  const { setVisibleChatGPTTooltip } = useChatGPTContext();

  const needConfirmation =
    loyalty?.loyaltyType &&
    !loyalty?.isActive &&
    loyalty.loyaltyType !== 'WorldPuan';

  const nonRefundableDatesText = () => {
    const startDate = nonRefundableDates?.[0]?.start;
    const endDate = nonRefundableDates?.[0]?.end;
    return `Kesinlikle iptal edilemez tarihler:
    ${formatISODateGTM(startDate ?? '')}
    ${getTimeOfISODate(startDate ?? '')} -
    ${formatISODateGTM(endDate ?? '')}
    ${getTimeOfISODate(endDate ?? '')}`;
  };

  const alertType = getAvailableStatus(status).alertType;
  const { isDesktop } = useDevice();

  const handleSelectVariation = (): void => {
    onSelectVariation && onSelectVariation();
    addHotelRoomSelectionClickEventToGTM({
      title: title || '',
      hotelName: hotelName || '',
      checkInDate: formatMomentDate(formCheckInDate, GTM_DATE_FORMAT),
      checkOutDate: formatMomentDate(formCheckOutDate, GTM_DATE_FORMAT),
      adultCount: hotelRoomList?.[0]?.adultCount || 0,
      childCount: hotelRoomList?.[0]?.childrenList.length || 0,
      price: prices?.price || prices?.originalPrice || 0,
      serviceType: 'Hotel',
    });
  };
  useEffect(() => {
    const body = document.querySelector('body');

    if (body) {
      if (showLoginRedirectionModal) {
        body.style.overflow = 'hidden';
      } else {
        body.style.overflow = 'initial';
      }
    }

    return () => {
      if (body) {
        body.style.overflow = 'initial';
      }
    };
  }, [showLoginRedirectionModal]);
  const goToTheTop = (): void => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    setTimeout(() => {
      openDatePicker?.(true);
    }, 1000);
  };

  const buttonAction = (
    status?: HotelAvailabilityStatusType
  ): ButtonActionType => {
    if (isSelected) {
      setVisibleChatGPTTooltip(false);
      return {
        text: 'Oda Seçildi',
      };
    } else if (
      needConfirmation &&
      !checkLoyaltyPreviouslyValidated(loyalty?.loyaltyType) &&
      !isLoggedIn &&
      loyalty?.loyaltyType === LoyaltyType.KOC_AILEM
    ) {
      return {
        text: 'Üye Ol',
        function: () => {
          signUpGtm('Kocailem - Hemen Üye Ol');
          setShowLoginRedirectionModal(true);
        },
      };
    } else if (
      needConfirmation &&
      !checkLoyaltyPreviouslyValidated(loyalty?.loyaltyType)
    ) {
      return {
        text: 'Doğrula',
        function: () => {
          if (requiredAuthentication(loyalty?.loyaltyType) && !loginInfo) {
            setShowLoginRedirectionModal(true);
          } else {
            setModalLoyaltyType(loyalty?.loyaltyType || null);
          }
        },
      };
    } else if (status) {
      return {
        text: getAvailableStatus(status).button,
        function:
          status === HotelAvailabilityStatusType.AVAILABLE
            ? handleSelectVariation
            : goToTheTop,
      };
    } else {
      return {
        text: 'Tarih Seç',
        function: goToTheTop,
      };
    }
  };

  const onAlertClick = () => {
    if (alertType === AlertType.PRIMARY || alertType === AlertType.SECONDARY) {
      setIsLetUsCallYouModalVisible(true);
      addHotelLetUsCallYouClickEventToGTM({
        title: title || '',
        hotelName: hotelName || '',
        checkInDate: formatMomentDate(formCheckInDate, GTM_DATE_FORMAT),
        checkOutDate: formatMomentDate(formCheckOutDate, GTM_DATE_FORMAT),
        adultCount: hotelRoomList?.[0]?.adultCount ?? 0,
        childCount: hotelRoomList?.[0]?.childrenList.length ?? 0,
        serviceType: 'Hotel',
      });
    }
  };

  const onCloseCallMeModal = () => {
    setIsLetUsCallYouModalVisible(false);
  };

  const onLetUsCallYouFormSendSuccess = () => {
    addHotelLetUsCallYouSuccessEventToGTM({
      title: title ?? '',
      hotelName: hotelName ?? '',
      checkInDate: formatMomentDate(formCheckInDate, GTM_DATE_FORMAT),
      checkOutDate: formatMomentDate(formCheckOutDate, GTM_DATE_FORMAT),
      serviceType: 'Hotel',
    });
  };

  const isLoyaltyValidated = checkLoyaltyPreviouslyValidated(
    loyalty?.loyaltyType
  );

  const TooltipText = (props) => {
    const {
      cancellationPolicyDetails,
      nonRefundableDates,
      isExpedia,
      hasCancelletion,
    } = props;
    if (!cancellationPolicyDetails?.[0]?.description) {
      return null;
    }
    if (cancellationPolicyDetails?.length || nonRefundableDates) {
      if (isExpedia) {
        if (cancellationPolicyDetails?.length && hasCancelletion) {
          return (
            <>
              {cancellationPolicies(cancellationPolicyDetails)()} Tarih / Saat
              bilgisi, otelin yerel zaman dilimine göre gösterilmektedir.
              {nonRefundableDates ? <>{nonRefundableDatesText()}</> : null}
            </>
          );
        } else if (nonRefundableDates) {
          return <>{nonRefundableDatesText()}</>;
        }
      } else {
        return cancellationPolicies(cancellationPolicyDetails)();
      }
    }
    return null;
  };

  const showMaxLoyaltyEarnPoints = useMemo(() => {
    return !isLoggedIn && (loyaltyEarn ?? 0) >= MAX_SETUR_POINTS_EARN;
  }, [isLoggedIn, loyaltyEarn]);

  if (
    shouldHideLoyalty(
      loyalty?.loyaltyType,
      query.utm_source as string,
      isLoyaltyValidated
    )
  )
    return null;

  const DailyDiscountedClick = (counter: number) => {
    if (counter % 2 === 0) setIsShow(false);
    else setIsShow(true);
    setCounter(counter + 1);
  };

  const showDailyPrices =
    dailyDiscountedPrice?.dailyDiscountedSellingPrices &&
    dailyDiscountedPrice?.dailyDiscountedSellingPrices.length > 0 &&
    !isExpedia &&
    !(needConfirmation && !isLoyaltyValidated);
  return (
    <Wrapper>
      {isDesktop ? (
        <DistantRow>
          <Col style={{ maxWidth: '743px', justifyContent: 'space-between' }}>
            <div style={{ display: 'inline-grid' }}>
              <Title>
                <Typography variant='caption1' color='D400' weight='Bold'>
                  {title}
                </Typography>
              </Title>
              <Description title={promotionInfoName}>
                <Typography variant='caption3' color='D400'>
                  {promotionInfoName}
                </Typography>
              </Description>
              {description ? (
                <ReadMoreDescription
                  description={description || ''}
                  initialVisibleLength={300}
                />
              ) : null}

              {roomSquareMeter ? (
                <Description>Oda büyüklüğü: {roomSquareMeter} m2</Description>
              ) : null}
              <Separator />
            </div>
            <div>
              {status === HotelAvailabilityStatusType.AVAILABLE &&
              !needConfirmation ? (
                <>
                  {childWithPriceCount != 0 ? (
                    <ChildPriceWrapper
                      dailyPrice={isExpedia}
                      bottomControl={!isShow}
                    >
                      <div>
                        <Circle />
                      </div>
                      <ChildPriceDesc>
                        <Typography variant='caption3' color='D400'>
                          Bu tesiste çocuk fiyatı bulunmamaktadır, eklenen
                          çocuk(lar), yetişkin fiyatı üzerinden
                          değerlendirilecektir.
                        </Typography>
                      </ChildPriceDesc>
                    </ChildPriceWrapper>
                  ) : null}
                  <LabelWrapper dailyPrice={isExpedia} bottomControl={!isShow}>
                    {roomInformation ? (
                      <MobileIconLabelWrapper>
                        <StyledIconLabel
                          roomCard={true}
                          backgroundColor='B50'
                          text={roomInformation}
                          icon={InfoIcon}
                          iconLocation='right'
                          iconColor='B300'
                          color='B300'
                          iconSize={16}
                        />
                      </MobileIconLabelWrapper>
                    ) : null}
                    <MobileWrapperForCancellation>
                      <StyledIconLabel
                        roomCard={true}
                        color={hasCancelletion ? 'G400' : 'tomato'}
                        iconColor={hasCancelletion ? 'G400' : 'tomato'}
                        backgroundColor={
                          hasCancelletion
                            ? 'veryLightGreenOpacity'
                            : 'veryLightCoral'
                        }
                        text={
                          hasCancelletion
                            ? 'İptal Edilebilir'
                            : 'İptal Edilemez'
                        }
                        icon={hasCancelletion ? InfoIcon : AlertIcon}
                        iconLocation={hasCancelletion ? 'right' : 'left'}
                        iconSize={16}
                        tooltipText={
                          TooltipText({
                            cancellationPolicyDetails,
                            nonRefundableDates,
                            isExpedia,
                            hasCancelletion,
                          }) ? (
                            <TooltipText
                              cancellationPolicyDetails={
                                cancellationPolicyDetails
                              }
                              nonRefundableDates={nonRefundableDates}
                              isExpedia={isExpedia}
                              hasCancelletion={hasCancelletion}
                            />
                          ) : null
                        }
                      />
                    </MobileWrapperForCancellation>
                  </LabelWrapper>
                </>
              ) : null}
            </div>
          </Col>

          <RightCol>
            {alertMessage ? (
              <WarningWrapper>
                <Alert
                  title={alertMessage}
                  type={alertType}
                  onClick={onAlertClick}
                />
              </WarningWrapper>
            ) : null}
            {!needConfirmation &&
            status === HotelAvailabilityStatusType.AVAILABLE ? (
              <LoyaltyEarnBadge
                discount={loyaltyDiscount ?? 0}
                earn={loyaltyEarn ?? 0}
                showMaxLoyaltyEarn={showMaxLoyaltyEarnPoints}
              />
            ) : null}
            {prices?.discount &&
            !needConfirmation &&
            (status ? isPriceAvailable(status) : null) ? (
              <>
                <Discount>{prices.discount}</Discount>
                <Separator />
              </>
            ) : null}
            {warningText ? (
              <WarningWrapper>
                <BorderedLabel
                  backgroundColor='veryLightFadedOrange'
                  color='fadedOrange'
                  borderColor='fadedOrange'
                  text={warningText}
                />
              </WarningWrapper>
            ) : null}
            {needConfirmation && !isLoyaltyValidated ? (
              <WarningWrapper>
                <BorderedLabel
                  backgroundColor='veryLightFadedOrange'
                  color='fadedOrange'
                  borderColor='fadedOrange'
                  text={`İndirimli fiyatı görmek için ${typeToName(
                    loyalty?.loyaltyType
                  )} hesabınızı doğrulayın.`}
                />
              </WarningWrapper>
            ) : prices?.price && (status ? isPriceAvailable(status) : null) ? (
              <>
                <Price
                  originalPrice={prices?.originalPrice}
                  price={prices?.price}
                  currency={getCurrencySymbol(prices?.currency)}
                  originalPriceCurrency={getCurrencySymbol(
                    prices.originalCurrency
                  )}
                >
                  {isExpedia ? (
                    <ExpediaPrice
                      originalPriceCurrency={getCurrencySymbol(
                        prices.originalCurrency
                      )}
                      originalPrice={prices?.originalPrice}
                      currency={getCurrencySymbol(prices.originalCurrency)}
                      price={prices?.price}
                    />
                  ) : (
                    <>
                      {prices?.originalPrice !== prices?.price ? (
                        <OriginalPrice
                          price={prices?.originalPrice}
                          currency={getCurrencySymbol(prices.originalCurrency)}
                          noLine={false}
                        />
                      ) : null}
                    </>
                  )}
                </Price>
                {basketHotelCount &&
                basketHotelCount >
                  0 ? null : bankPosOpportunityInfo?.sellingPrice &&
                  bankPosPromotionRate ? (
                  <WorldPriceWrapper>
                    <WorldPriceTextWrapper>
                      <Tooltip
                        overlayInnerStyle={{
                          textAlign: 'left',
                          backgroundColor: palette.NL0,
                          padding: '8px',
                          width: '250px',
                        }}
                        arrowColor={palette.NL0}
                        arrowLeft={10}
                        overlayStyle={{
                          backgroundColor: palette.NL0,
                          borderRadius: '8px',
                          width: '250px',
                        }}
                        placement='bottomLeft'
                        title={
                          <Typography
                            variant='caption2'
                            weight='Light'
                            color='D500'
                            as='div'
                            style={{
                              lineHeight: 'normal',
                              width: '100%',
                              textWrapMode: 'wrap',
                              textAlign: 'center',
                            }}
                          >
                            Seyahat planlama sayfasında World Kart’a özel
                            indirim seçimini yaparak indirimden
                            faydalanabilirsiniz.
                          </Typography>
                        }
                        getPopupContainer={(node) => node}
                      >
                        <CustomIcon
                          paletteColor='NL0'
                          icon={InfoIcon}
                          fontSize={18}
                        />
                      </Tooltip>
                      <CustomIcon
                        icon={WorldLogoWhite}
                        width={56}
                        height={9}
                        color='NL0'
                      />
                      <Typography
                        variant='caption3'
                        weight='Bold'
                        color='NL0'
                        style={{ lineHeight: 'normal' }}
                      >
                        {`'e Özel %${bankPosPromotionRate}`}
                      </Typography>
                    </WorldPriceTextWrapper>
                    <SeparatorWorld />
                    <Price
                      price={bankPosOpportunityInfo?.sellingPrice}
                      currency={getCurrencySymbol('tl')}
                      color='NL0'
                    />
                  </WorldPriceWrapper>
                ) : null}
                {feeDetails ? <FeeDetails feeDetails={feeDetails} /> : null}
                <Separator />
              </>
            ) : null}
            {status !== HotelAvailabilityStatusType.N_A ? (
              <StyledButtonDesktop
                variant='Primary'
                isSelected={isSelected}
                onClick={buttonAction(status).function}
              >
                {buttonAction(status).text}
              </StyledButtonDesktop>
            ) : null}
            {showDailyPrices ? (
              <DailyDiscountedButton
                isShow={isShow}
                onClick={() => DailyDiscountedClick(counter)}
              >
                <ButtonWrapper>
                  {isShow ? (
                    <Typography
                      variant='caption2'
                      color='D400'
                      weight='SemiBold'
                    >
                      Gecelik Fiyatları Göster
                    </Typography>
                  ) : (
                    <Typography
                      variant='caption2'
                      color='D400'
                      weight='SemiBold'
                    >
                      Gecelik Fiyatları Gizle
                    </Typography>
                  )}
                  <CustomIcon
                    icon={isShow ? DownArrowIcon : UpArrowIcon}
                    fontSize={16}
                  />
                </ButtonWrapper>
              </DailyDiscountedButton>
            ) : null}
          </RightCol>
        </DistantRow>
      ) : (
        <DistantColumn>
          <Col style={{ maxWidth: '743px', justifyContent: 'space-between' }}>
            <InlineContainer>
              <RoomCardInfoWrapper>
                <Title>
                  <PromDescriptionTitle>{title}</PromDescriptionTitle>
                </Title>
                <PromDescription title={promotionInfoName}>
                  <Typography variant='caption3' color='D400'>
                    {promotionInfoName}
                  </Typography>
                </PromDescription>
              </RoomCardInfoWrapper>
              {description ? (
                <ReadMoreDescription
                  description={description ?? ''}
                  initialVisibleLength={120}
                />
              ) : null}

              {roomSquareMeter ? (
                <Description>Oda büyüklüğü: {roomSquareMeter} m2</Description>
              ) : null}
              <Separator />

              {!needConfirmation &&
              status === HotelAvailabilityStatusType.AVAILABLE ? (
                <LoyaltyEarnBadge
                  discount={loyaltyDiscount ?? 0}
                  earn={loyaltyEarn ?? 0}
                  showMaxLoyaltyEarn={showMaxLoyaltyEarnPoints}
                />
              ) : null}
            </InlineContainer>
            <div>
              {status === HotelAvailabilityStatusType.AVAILABLE &&
              !needConfirmation ? (
                <>
                  {childWithPriceCount != 0 ? (
                    <ChildPriceWrapper
                      dailyPrice={isExpedia}
                      bottomControl={!isShow}
                    >
                      <div>
                        <Circle />
                      </div>
                      <ChildPriceDesc>
                        <Typography variant='caption3' color='D400'>
                          Bu tesiste çocuk fiyatı bulunmamaktadır, eklenen
                          çocuk(lar), yetişkin fiyatı üzerinden
                          değerlendirilecektir.
                        </Typography>
                      </ChildPriceDesc>
                    </ChildPriceWrapper>
                  ) : null}
                  <LabelWrapper dailyPrice={isExpedia} bottomControl={!isShow}>
                    {roomInformation ? (
                      <MobileIconLabelWrapper>
                        <StyledIconLabel
                          roomCard={true}
                          backgroundColor='B50'
                          text={roomInformation}
                          icon={InfoIcon}
                          iconLocation='right'
                          iconColor='B300'
                          color='B300'
                          iconSize={16}
                        />
                      </MobileIconLabelWrapper>
                    ) : null}
                    <MobileWrapperForCancellation>
                      <StyledIconLabel
                        roomCard={true}
                        color={hasCancelletion ? 'G400' : 'tomato'}
                        iconColor={hasCancelletion ? 'G400' : 'tomato'}
                        backgroundColor={
                          hasCancelletion
                            ? 'veryLightGreenOpacity'
                            : 'veryLightCoral'
                        }
                        text={
                          hasCancelletion
                            ? 'İptal Edilebilir'
                            : 'İptal Edilemez'
                        }
                        icon={hasCancelletion ? InfoIcon : AlertIcon}
                        iconLocation={hasCancelletion ? 'right' : 'left'}
                        iconSize={16}
                        tooltipText={
                          TooltipText({
                            cancellationPolicyDetails,
                            nonRefundableDates,
                            isExpedia,
                            hasCancelletion,
                          }) ? (
                            <TooltipText
                              cancellationPolicyDetails={
                                cancellationPolicyDetails
                              }
                              nonRefundableDates={nonRefundableDates}
                              isExpedia={isExpedia}
                              hasCancelletion={hasCancelletion}
                            />
                          ) : null
                        }
                      />
                    </MobileWrapperForCancellation>
                  </LabelWrapper>
                </>
              ) : null}
            </div>
          </Col>

          <RightCol>
            {alertMessage ? (
              <WarningWrapper>
                <Alert
                  title={alertMessage}
                  type={alertType}
                  onClick={onAlertClick}
                />
              </WarningWrapper>
            ) : null}

            {prices?.discount &&
            !needConfirmation &&
            (status ? isPriceAvailable(status) : null) ? (
              <>
                <Discount>{prices.discount}</Discount>
                <Separator />
              </>
            ) : (
              <Separator size='40px' />
            )}
            {warningText ? (
              <WarningWrapper>
                <BorderedLabel
                  backgroundColor='veryLightFadedOrange'
                  color='fadedOrange'
                  borderColor='fadedOrange'
                  text={warningText}
                />
              </WarningWrapper>
            ) : null}
            {needConfirmation && !isLoyaltyValidated ? (
              <WarningWrapper>
                <BorderedLabel
                  backgroundColor='veryLightFadedOrange'
                  color='fadedOrange'
                  borderColor='fadedOrange'
                  text={`İndirimli fiyatı görmek için ${typeToName(
                    loyalty?.loyaltyType
                  )} hesabınızı doğrulayın.`}
                />
              </WarningWrapper>
            ) : prices?.price && (status ? isPriceAvailable(status) : null) ? (
              <>
                <Price
                  originalPrice={prices?.originalPrice}
                  price={prices?.price}
                  currency={getCurrencySymbol(prices?.currency)}
                  originalPriceCurrency={getCurrencySymbol(
                    prices.originalCurrency
                  )}
                >
                  {isExpedia ? (
                    <ExpediaPrice
                      originalPriceCurrency={getCurrencySymbol(
                        prices.originalCurrency
                      )}
                      originalPrice={prices?.originalPrice}
                      currency={getCurrencySymbol(prices.originalCurrency)}
                      price={prices?.price}
                    />
                  ) : (
                    <>
                      {prices?.originalPrice !== prices?.price ? (
                        <OriginalPrice
                          price={prices?.originalPrice}
                          currency={getCurrencySymbol(prices.originalCurrency)}
                          noLine={false}
                        />
                      ) : null}
                    </>
                  )}
                </Price>
                {basketHotelCount &&
                basketHotelCount >
                  0 ? null : bankPosOpportunityInfo?.sellingPrice &&
                  bankPosPromotionRate ? (
                  <WorldPriceWrapperMobile>
                    <Tooltip
                      overlayInnerStyle={{
                        textAlign: 'left',
                        backgroundColor: palette.NL0,
                        padding: '8px',
                        width: '250px',
                      }}
                      arrowColor={palette.NL0}
                      arrowLeft={10}
                      overlayStyle={{
                        backgroundColor: palette.NL0,
                        borderRadius: '8px',
                        width: '250px',
                      }}
                      placement='bottomLeft'
                      title={
                        <Typography
                          variant='caption2'
                          weight='Light'
                          color='D500'
                          as='div'
                          style={{
                            lineHeight: 'normal',
                            width: '100%',
                            textWrapMode: 'wrap',
                            textAlign: 'center',
                          }}
                        >
                          Seyahat planlama sayfasında World Kart’a özel indirim
                          seçimini yaparak indirimden faydalanabilirsiniz.
                        </Typography>
                      }
                      getPopupContainer={(node) => node}
                    >
                      <CustomIcon
                        paletteColor='NL0'
                        icon={InfoIcon}
                        fontSize={18}
                      />
                    </Tooltip>
                    <WorldPriceContainerMobile>
                      <WorldPriceTextWrapperMobile>
                        <CustomIcon
                          icon={WorldLogoWhite}
                          width={56}
                          height={9}
                          color='NL0'
                        />
                        <Typography
                          variant='caption3'
                          weight='Bold'
                          color='NL0'
                          style={{ lineHeight: 'normal' }}
                        >
                          'e
                        </Typography>
                      </WorldPriceTextWrapperMobile>
                      <Typography
                        variant='caption3'
                        weight='Bold'
                        color='NL0'
                        style={{ lineHeight: 'normal' }}
                      >
                        {`Özel %${bankPosPromotionRate}`}
                      </Typography>
                    </WorldPriceContainerMobile>
                    <SeparatorWorld />
                    <Price
                      price={bankPosOpportunityInfo?.sellingPrice}
                      currency={getCurrencySymbol('tl')}
                      color='NL0'
                      fontSize={24}
                    />
                  </WorldPriceWrapperMobile>
                ) : null}
                {feeDetails ? <FeeDetails feeDetails={feeDetails} /> : null}
                <Separator />
              </>
            ) : null}
            {status !== HotelAvailabilityStatusType.N_A ? (
              <StyledButtonMobile
                variant='Primary'
                isSelected={isSelected}
                onClick={buttonAction(status).function}
              >
                {buttonAction(status).text}
              </StyledButtonMobile>
            ) : null}
            {showDailyPrices ? (
              <DailyDiscountedButton
                isShow={isShow}
                onClick={() => DailyDiscountedClick(counter)}
              >
                <ButtonWrapper>
                  {isShow ? (
                    <Typography
                      variant='caption2'
                      color='D400'
                      weight='SemiBold'
                    >
                      Gecelik Fiyatları Göster
                    </Typography>
                  ) : (
                    <Typography
                      variant='caption2'
                      color='D400'
                      weight='SemiBold'
                    >
                      Gecelik Fiyatları Gizle
                    </Typography>
                  )}
                  <CustomIcon
                    icon={isShow ? DownArrowIcon : UpArrowIcon}
                    fontSize={16}
                  />
                </ButtonWrapper>
              </DailyDiscountedButton>
            ) : null}
          </RightCol>
        </DistantColumn>
      )}

      {showDailyPrices ? (
        <PriceContainer show={isShow}>
          <StyledSwiper
            loop={false}
            spaceBetween={10}
            slidesPerView={isDesktop ? 7.0 : 2.3}
            navigation={isDesktop}
          >
            {dailyDiscountedPrice?.dailyDiscountedSellingPrices?.map(
              (i, index) => (
                <SwiperSlide key={index}>
                  <PriceCard
                    date={formatISOToDate(i.date, DEFAULT_DATE_FORMAT)}
                    day={moment(i.date).format('dddd')}
                    discounted={i.tryDiscountedSellingPrice}
                    price={i.trySellingPrice}
                    currency='TL'
                    status={getDateStatus(
                      availableDates,
                      formatISOToDate(i.date, DEFAULT_DATE_FORMAT)
                    )}
                  />
                </SwiperSlide>
              )
            )}
          </StyledSwiper>

          <DateStatusTypes />
        </PriceContainer>
      ) : null}
      {id && formCheckInDate && formCheckOutDate ? (
        <LetUsCallYou
          visibleLetUsCallYouModal={isLetUsCallYouModalVisible}
          token=''
          onClose={onCloseCallMeModal}
          onFormSendSuccess={onLetUsCallYouFormSendSuccess}
          entityId={id.toString()}
          entityName={hotelName + ' | ' + title + ' Odası ' || ''}
          currency={Currencies.TL}
          price={prices?.price || 0}
          salesItemType={C4CSalesItemType.OTEL_YURT_ICI}
          availabilityStatus={status}
          formCheckInDate={formCheckInDate}
          formCheckOutDate={formCheckOutDate}
          hotelRoomList={hotelRoomList || []}
          alertType={alertType}
        />
      ) : null}
      {showLoginRedirectionModal ? (
        <LoginRedirectModal
          onCancel={() => setShowLoginRedirectionModal(false)}
          loyaltyType={loyalty?.loyaltyType}
        />
      ) : null}
    </Wrapper>
  );
};

export default RoomSelectionCard;
